import React, { useEffect, useState } from 'react';
import { Alert, ActivityIndicator, Platform } from 'react-native';
import { useRouter } from 'expo-router';
import * as Linking from 'expo-linking';
import { useTranslation } from 'react-i18next';
import '../../i18n';
import { useAuth } from '../../features/auth/useAuth';
import { supabase } from '../../lib/supabase';
import { parseFragmentParams } from '../shared/parseFragmentParams';
import { Screen } from '../shared/Screen';
import { Heading, Subtitle } from '../shared/Typography';
import { TextField } from '../shared/TextField';
import { Button } from '../shared/Button';

/**
 * Tela de "nova senha", aberta pelo deep link do e-mail de recuperação
 * (spec S01, `useAuth.passwordReset` → `redirectTo`).
 *
 * Por que `setSession` e não escutar o evento `PASSWORD_RECOVERY`: o client
 * (`src/lib/supabase.ts`) não define `flowType: 'pkce'` (fica no default
 * `'implicit'` do supabase-js 2.110.x) e usa `detectSessionInUrl: false`
 * (correto para React Native — não existe `window.location` aqui). Nessa
 * configuração o GoTrue nunca emite `PASSWORD_RECOVERY` sozinho: esse evento
 * só sai de `_initialize()` quando `detectSessionInUrl` está ligado num
 * browser, ou de `exchangeCodeForSession`/`verifyOtp` quando quem chamou
 * pediu por ele explicitamente — nenhum dos dois é o caminho automático
 * aqui. O que o GoTrue realmente entrega, com o template de e-mail padrão
 * (não há template customizado em `supabase/`), é um redirect para
 * `redirectTo` com a sessão anexada como fragmento da URL
 * (`#access_token=...&refresh_token=...&type=recovery`), exatamente como o
 * fluxo implícito de login. Por isso esta tela lê a URL de abertura do app
 * (via `expo-linking`, que não expõe o fragmento em `queryParams` — dá pra
 * conferir em `parse()` do pacote instalado), extrai os tokens manualmente
 * e chama `setSession` (que emite `SIGNED_IN`, não `PASSWORD_RECOVERY`) —
 * daí o gate de auth em `app/_layout.tsx` precisar de um corte específico
 * para essa rota, para não tratar isso como um login normal.
 *
 * ponytail: se o time trocar o template de e-mail no painel do Supabase
 * para usar `{{ .TokenHash }}` (link próprio do app, sem passar pelo
 * `/verify` do GoTrue), o caminho mais simples passa a ser
 * `verifyOtp({ token_hash, type: 'recovery' })` a partir de
 * `useLocalSearchParams()` do expo-router — evita todo este parsing manual
 * de fragmento e o risco de o navegador do aparelho descartar o `#` no
 * redirect. Não fiz isso agora porque não há template customizado neste
 * repo; é o upgrade natural se a verificação manual (ver resumo da tarefa)
 * mostrar o fragmento sendo perdido no dispositivo.
 */

type Status = 'linking' | 'form' | 'submitting' | 'error';

const LINK_TIMEOUT_MS = 8000;

export function ResetPasswordScreen() {
  const { t } = useTranslation();
  const router = useRouter();
  const { completePasswordRecovery, updatePassword } = useAuth();
  const [status, setStatus] = useState<Status>('linking');
  const [errorMessage, setErrorMessage] = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => {
    let cancelled = false;
    let resolved = false;

    async function handleUrl(url: string | null) {
      if (!url || cancelled || resolved) return;
      const params = parseFragmentParams(url);
      if (!params.access_token && !params.refresh_token && !params.code && !params.token_hash && !params.error) {
        return;
      }

      resolved = true;

      if (params.error) {
        setErrorMessage(params.error_description || params.error || t('auth.resetPassword.invalidLinkMessage'));
        setStatus('error');
        return;
      }

      try {
        if (params.access_token && params.refresh_token) {
          await completePasswordRecovery(params.access_token, params.refresh_token);
        } else if (params.code) {
          const { error } = await supabase.auth.exchangeCodeForSession(params.code);
          if (error) throw error;
        } else if (params.token_hash) {
          const { error } = await supabase.auth.verifyOtp({
            token_hash: params.token_hash,
            type: 'recovery',
          });
          if (error) throw error;
        } else {
          setErrorMessage(t('auth.resetPassword.invalidLinkMessage'));
          setStatus('error');
          return;
        }

        if (cancelled) return;
        setStatus('form');
      } catch (error) {
        if (cancelled) return;
        setErrorMessage((error as Error).message);
        setStatus('error');
      }
    }

    if (Platform.OS === 'web' && typeof window !== 'undefined' && window.location.href) {
      handleUrl(window.location.href);
    }
    Linking.getInitialURL().then(handleUrl);
    const subscription = Linking.addEventListener('url', (event) => handleUrl(event.url));

    // Tela aberta sem vir de um link de e-mail válido (ou o link não trouxe
    // nenhum token/erro reconhecível): não trava em spinner infinito.
    const timeout = setTimeout(() => {
      if (!cancelled && !resolved) {
        setErrorMessage(t('auth.resetPassword.invalidLinkMessage'));
        setStatus('error');
      }
    }, LINK_TIMEOUT_MS);

    return () => {
      cancelled = true;
      subscription.remove();
      clearTimeout(timeout);
    };
  }, [completePasswordRecovery, t]);

  const handleSubmit = async () => {
    if (!password) {
      Alert.alert(t('auth.resetPassword.requiredFieldTitle'), t('auth.resetPassword.requiredFieldMessage'));
      return;
    }
    if (password.length < 8) {
      Alert.alert(t('auth.resetPassword.shortPasswordTitle'), t('auth.resetPassword.shortPasswordMessage'));
      return;
    }
    setStatus('submitting');
    try {
      await updatePassword(password);
      router.replace('/(tabs)');
    } catch (error) {
      Alert.alert(t('auth.resetPassword.errorTitle'), (error as Error).message);
      setStatus('form');
    }
  };

  if (status === 'linking') {
    return (
      <Screen>
        <ActivityIndicator size="large" />
        <Subtitle>{t('auth.resetPassword.processingMessage')}</Subtitle>
      </Screen>
    );
  }

  if (status === 'error') {
    return (
      <Screen>
        <Heading>{t('auth.resetPassword.invalidLinkTitle')}</Heading>
        <Subtitle>{errorMessage}</Subtitle>
        <Button
          variant="ghost"
          title={t('auth.resetPassword.backToRecovery')}
          onPress={() => router.push('/(auth)/recover')}
        />
      </Screen>
    );
  }

  return (
    <Screen>
      <Heading>{t('auth.resetPassword.title')}</Heading>
      <Subtitle>{t('auth.resetPassword.subtitle')}</Subtitle>
      <TextField
        placeholder={t('auth.resetPassword.passwordPlaceholder')}
        value={password}
        onChangeText={setPassword}
        secureTextEntry
      />
      {status === 'submitting' ? (
        <ActivityIndicator />
      ) : (
        <Button title={t('auth.resetPassword.submit')} onPress={handleSubmit} />
      )}
    </Screen>
  );
}
